import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { X, Check } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { errMsg } from "@/lib/api";

/**
 * Formulario minimalista de interés (etapa exhibición / Digital Fashion Exhibition).
 * Sin talles, sin colores, sin datos de compra. Solo identidad + contacto + consentimientos.
 */
export function InterestModal({ piece, open, onClose, onSuccess }) {
  const [f, setF] = useState({ name: "", email: "", whatsapp: "" });
  const [cEmail, setCEmail] = useState(true);
  const [cWsp, setCWsp] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !piece) return null;

  const submit = async () => {
    if (!f.email.trim()) return toast.error("Tu email es lo único obligatorio");
    if (!cEmail && !cWsp) return toast.error("Aceptá al menos un medio de contacto para avisarte");
    setBusy(true);
    try {
      const r = await api.post("/interest", {
        product_id: piece.id, name: f.name, email: f.email, whatsapp: f.whatsapp,
        consent_email: cEmail, consent_whatsapp: cWsp, website: "",
      });
      setDone(true);
      if (onSuccess) onSuccess();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo registrar tu interés");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="interest-modal">
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
        className="relative museum-frame bg-[#0F0F0F] p-8 max-w-md w-full">
        <button onClick={onClose} aria-label="Cerrar" className="absolute top-4 right-4 text-[#565A61] hover:text-[#171719]">
          <X size={20} />
        </button>
        {done ? (
          <div className="text-center py-6" data-testid="interest-success">
            <div className="w-12 h-12 mx-auto rounded-full border border-[#72B078] flex items-center justify-center">
              <Check size={22} className="text-[#4C7A51]" />
            </div>
            <p className="font-script text-3xl text-[#171719] mt-6">Registramos tu interés.</p>
            <p className="text-[#565A61] text-sm mt-4">{"You'll be among the first to access this piece."}</p>
            <p className="dossier-label text-[#565A61] mt-4">STAY CLOSE</p>
            <p className="font-display text-xl text-[#171719] mt-6 tracking-tight">ARCHIVE <span className="font-script">lab</span></p>
            <button onClick={onClose} className="mt-8 btn-outline-ink px-6 py-3 dossier-label">Cerrar</button>
          </div>
        ) : (
          <>
            <div className="dossier-label text-[#565A61]">Manifestá tu interés</div>
            <h3 className="font-display text-2xl text-[#171719] uppercase tracking-tight mt-2">{piece.name}</h3>
            <p className="text-[11px] text-[#565A61] font-mono2 mt-1">{piece.design_code}</p>
            <div className="mt-6 space-y-4">
              <div>
                <Label className="dossier-label text-[10px] text-[#565A61]">Nombre</Label>
                <Input data-testid="interest-name" className="mt-2 bg-[#CFD1D6] border-[#C4C7CD] text-[#171719]" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Tu nombre" />
              </div>
              <div>
                <Label className="dossier-label text-[10px] text-[#565A61]">Email *</Label>
                <Input data-testid="interest-email" type="email" className="mt-2 bg-[#CFD1D6] border-[#C4C7CD] text-[#171719]" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="tu@email.com" />
              </div>
              <div>
                <Label className="dossier-label text-[10px] text-[#565A61]">WhatsApp (opcional)</Label>
                <Input data-testid="interest-whatsapp" type="tel" className="mt-2 bg-[#CFD1D6] border-[#C4C7CD] text-[#171719]" value={f.whatsapp} onChange={(e) => setF({ ...f, whatsapp: e.target.value })} placeholder="+54 9 11 …" />
              </div>
              <div className="space-y-2 pt-2">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input data-testid="interest-consent-email" type="checkbox" checked={cEmail} onChange={(e) => setCEmail(e.target.checked)} className="w-4 h-4 mt-0.5" />
                  <span className="text-xs text-[#565A61]">Quiero recibir novedades por email</span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input data-testid="interest-consent-wsp" type="checkbox" checked={cWsp} onChange={(e) => setCWsp(e.target.checked)} className="w-4 h-4 mt-0.5" />
                  <span className="text-xs text-[#565A61]">Quiero recibir novedades por WhatsApp</span>
                </label>
              </div>
              <button data-testid="interest-submit" onClick={submit} disabled={busy} className="btn-ink w-full px-6 py-4 dossier-label">
                {busy ? "Registrando…" : "QUIERO ESTA PIEZA"}
              </button>
              <p className="text-[10px] text-[#858A91] text-center">Sin talles, sin pagos todavía. Solo tu interés, registrado en el archivo.</p>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}
